import MonthRangeSelector, { MonthRange } from '@/components/MonthRangeSelector'
import AsyncSelect from '@/components/Form/Shared/AsyncSelect'
import EmployeeSelect from '@/components/Form/Shared/EmployeeSelect'
import { getProductsGroupOptions } from '@/modules/ProductGroup/services'
import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { FormItem } from 'vnetwork-ui'
import { DEPARTMENT_IDS } from '@/constants/departments'
interface RevenueFiltersForm {
    sale_manager_codes?: number[]
    account_manager_codes?: number[]
    product_group_names?: string[]
    is_actual_revenue?: boolean
    revenue_type?: string
    is_allocated?: boolean
    is_paid?: boolean
}

interface FiltersProps {
    components?: React.ReactNode[]
    excludeOptions?: string[]
    onFiltersChange?: (filters: any) => void
    initialFilters?: Partial<RevenueFiltersForm & MonthRange>
}

const Filters: React.FC<FiltersProps> = ({
    onFiltersChange,
    initialFilters,
    excludeOptions = [],
    components = [],
}) => {
    const { t } = useTranslation()
    const { control, watch } = useForm<RevenueFiltersForm>({
        defaultValues: initialFilters || {},
    })

    const [monthRange, setMonthRange] = useState<MonthRange>({
        gte: initialFilters?.gte || '',
        lte: initialFilters?.lte || '',
    })

    // Watch form changes and notify parent
    React.useEffect(() => {
        const subscription = watch((data) => {
            if (onFiltersChange) {
                onFiltersChange({
                    ...data,
                    gte: monthRange.gte,
                    lte: monthRange.lte,
                })
            }
        })
        return () => subscription.unsubscribe()
    }, [watch, onFiltersChange, monthRange])

    // Handle month range changes
    const handleMonthRangeChange = (range: MonthRange) => {
        setMonthRange(range)
        if (onFiltersChange) {
            const currentData = watch()
            onFiltersChange({
                ...currentData,
                gte: range.gte,
                lte: range.lte,
            })
        }
    }

    return (
        <div className="bg-white rounded-md shadow-sm p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
                {!excludeOptions?.includes('product_group_names') && (
                    <AsyncSelect
                        control={control}
                        asyncOptions={getProductsGroupOptions}
                        name="product_group_names"
                        multiple
                        label={t('product_group_name')}
                        placeholder={t('product_group')}
                    />
                )}

                {/* Sale Manager Select */}
                {!excludeOptions?.includes('sale_manager_codes') && (
                    <EmployeeSelect
                        control={control}
                        multiple
                        onlyActive
                        departmentIds={[
                            DEPARTMENT_IDS.SALES,
                            DEPARTMENT_IDS.ACCOUNT,
                            DEPARTMENT_IDS.BOARD_OF_DIRECTORS,
                        ]}
                        name="sale_manager_codes"
                        label={t('sale_manager_codes')}
                        showLabel
                    />
                )}
                {!excludeOptions?.includes('account_manager_codes') && (
                    <EmployeeSelect
                        control={control}
                        multiple
                        onlyActive
                        departmentIds={[DEPARTMENT_IDS.ACCOUNT]}
                        name="account_manager_codes"
                        label={t('account_manager_codes')}
                        showLabel
                    />
                )}
                {!excludeOptions?.includes('is_actual_revenue') && (
                    <FormItem
                        control={control}
                        name="is_actual_revenue"
                        type="checkbox"
                        label={t('only_actual_revenue')}
                    />
                )}
                {!excludeOptions?.includes('payment_status') && (
                    <FormItem
                        control={control}
                        name="is_paid"
                        type="select"
                        placeholder={t('payment_status')}
                        label={t('payment_status')}
                        options={[
                            { label: t('paid'), value: true },
                            { label: t('unpaid'), value: false },
                        ]}
                    />
                )}
                {!excludeOptions?.includes('payment_status') && (
                    <FormItem
                        control={control}
                        name="is_allocated"
                        type="select"
                        placeholder={t('allocations.allocated')}
                        label={t('allocations.allocated')}
                        options={[
                            { label: t('allocated'), value: true },
                            { label: t('not_allocated'), value: false },
                        ]}
                    />
                )}
                <MonthRangeSelector
                    value={monthRange}
                    onChange={handleMonthRangeChange}
                    placement="left"
                />
                {components.map((component, index) => (
                    <div key={index} className="col-span-1">
                        {component}
                    </div>
                ))}
            </div>
        </div>
    )
}

export default Filters
